// core/shell-open.js — R1b.next: route an opened .torrent/magnet to the player.
//
// Two entrypoints converge on routeToPlayer():
//   * Native (embedded): the shell uploaded the .torrent/magnet itself (Commit 12)
//     and fires an "openExternal" event with the small result — initShellOpen()
//     listens for it. It also proactively pushes the access token to the shell so
//     the native uploader has a Bearer.
//   * Web (browser dialog/drop, Commit 15): handleTorrentInput() uploads via
//     torrent-add.js and routes the result.
// A magnet has no metadata yet, so we poll GET /v1/torrent/<id> until has_metadata
// and build the torrent:// URI ourselves.
import { openPlayer } from '../player/open-player.js';
import { auth } from './auth.js';
import { toast } from './toast.js';
import { addMagnet, addTorrentBlob, torrentStatus } from './torrent-add.js';

const META_POLL_MS = 1500;
const META_TIMEOUT_MS = 90000;

function playerOpts(extra) {
  return {
    kind: 'torrent',
    getToken: () => auth.getToken(),
    onTokenChange: (cb) => auth.onTokenChange(cb),
    ...extra,
  };
}

function launch(torrentUri, name, torrentId) {
  return openPlayer(playerOpts({ path: torrentUri, name, torrentId }));
}

// Blu-ray/DVD disc image (.iso): open the on-disk path with NO torrent kind so
// the native session detects the .iso suffix and routes it through /disc/open.
// The server reads the main-title .m2ts straight out of the .iso via udfread (no
// loop-mount). A raw .iso can't be demuxed as a byte stream, so the torrent
// stream path never works for discs — but /disc/open does play-while-download:
// if the .iso is still downloading it reads through the gated torrent so udfread
// pulls the UDF structure + title on-demand (front + tail), no full file needed.
function launchDisc(discPath, name) {
  return openPlayer({
    path: discPath,
    name,
    getToken: () => auth.getToken(),
    onTokenChange: (cb) => auth.onTokenChange(cb),
  });
}

// Poll a magnet's metadata until has_metadata, then build torrent://<hash>/<idx>
// and open the player. A sticky toast shows progress and is dismissed at the end.
async function awaitMetadataThenPlay(torrentId, name) {
  const dismiss = toast.info('Metadaten werden geladen…', { sticky: true });
  const t0 = Date.now();
  try {
    for (;;) {
      const t = await torrentStatus(torrentId);
      if (t && t.has_metadata) {
        const hash = t.info_hash;
        const idx = t.media_file_index;
        if (!hash || idx == null || idx < 0) {
          throw new Error('kein abspielbares Video im Torrent');
        }
        dismiss();
        launch('torrent://' + hash + '/' + idx, name || t.name, torrentId);
        return;
      }
      if (Date.now() - t0 > META_TIMEOUT_MS) {
        throw new Error('Zeitüberschreitung beim Laden der Metadaten');
      }
      await new Promise((res) => setTimeout(res, META_POLL_MS));
    }
  } catch (e) {
    dismiss();
    toast.error('Torrent: ' + (e?.message || e));
  }
}

// Route an add-result (native openExternal event OR web add) to the player.
function routeToPlayer(data) {
  if (!data) return;
  // Disc image (.iso) → disc-play path (loop-mount + main title), not the raw
  // torrent stream. The native openExternal event carries disc_path at the top
  // level; the web add-result carries it under media.
  const disc = data.disc_path || data.media?.disc_path;
  if (disc) {
    launchDisc(disc, data.name);
    return;
  }
  const uri = data.torrent_uri || data.media?.torrent_uri;
  if (uri) {
    launch(uri, data.name, data.torrent_id);
    return;
  }
  if (data.needs_metadata && data.torrent_id != null) {
    awaitMetadataThenPlay(data.torrent_id, data.name);
    return;
  }
  toast.error('Torrent: kein abspielbares Video gefunden');
}

// Web path (Commit 15 dialog / drop): input is a File (.torrent) or a magnet: URI.
// opts.import=true marks a library add (S3 Serien) — sorted into the series
// tree when the download finishes.
export async function handleTorrentInput(input, opts = {}) {
  try {
    let data;
    if (typeof input === 'string') {
      if (!/^magnet:/i.test(input.trim())) {
        toast.error('Kein gültiger Magnet-Link');
        return;
      }
      data = await addMagnet(input.trim(), opts);
    } else if (input instanceof File || input instanceof Blob) {
      data = await addTorrentBlob(input, opts);
    } else {
      return;
    }
    routeToPlayer(data);
  } catch (e) {
    toast.error('Torrent: ' + (e?.message || e));
  }
}

// Init: listen for the native openExternal result + push the access token to
// the shell so the native uploader has a Bearer. The shim injects
// window.zapShell AFTER the SPA bundle boots (WKWebView runJavaScript runs
// post-load), so a single presence check races the injection — poll briefly
// until the shell appears instead. In a plain browser it never does; the poll
// runs out quietly.
// Native home (FilmBrowse.qml) plays a library item through the SAME proven
// pipeline as every SPA view: openPlayer -> NativeShellSession (disc-vs-stream
// routing, byte-accurate seek index, play-while-download 503 retry, source-drop
// recovery, resume, token re-point, close). The native QML calls this over the
// runJavaScript bridge with the item's on-disk path; a plain .iso path is
// detected inside the session and routed through /disc/open. resumeSec carries
// the server-side watch-progress so the "Weiterschauen ab MM:SS" is honored
// even on a fresh device (localStorage would be empty there).
function zapShellPlay(item) {
  try {
    const path = item && item.path;
    if (!path) return;
    if (globalThis.__zapNativePlayer) {
      try {
        globalThis.__zapNativePlayer.destroy();
      } catch {
        /* noop */
      }
    }
    globalThis.__zapNativePlayer = openPlayer({
      path,
      name: item.name || '',
      ext: item.ext || String(path).split('.').pop() || '',
      // Video codec id from the library row — native-session uses it to route
      // iOS-HW-undecodable codecs (MPEG-2/VC-1/…) through the HLS transcode.
      codec: item.codec || '',
      resumeSec: Number(item.resumeSec) || 0,
      getToken: () => auth.getToken(),
      onTokenChange: (cb) => auth.onTokenChange(cb),
      onClose: () => {
        globalThis.__zapNativePlayer = null;
      },
    });
  } catch (e) {
    toast.error('Wiedergabe: ' + (e?.message || e));
  }
}

export function initShellOpen({ onTorrentImported } = {}) {
  globalThis.zapShellPlay = zapShellPlay;
  let tries = 0;
  const arm = () => {
    const shell = globalThis.zapShell;
    if (!shell) {
      if (++tries < 120) setTimeout(arm, 250); // ~30 s window
      return;
    }
    if (typeof shell.setAuthToken === 'function') {
      const push = (tok) => {
        if (tok) shell.setAuthToken(tok);
      };
      push(auth.getToken());
      auth.onTokenChange(push);
    }
    if (typeof shell.on === 'function') {
      shell.on('openExternal', (data) => routeToPlayer(data));
      shell.on('openExternalError', (e) =>
        toast.error('Torrent: ' + (e?.err || 'Öffnen fehlgeschlagen')),
      );
      shell.on('torrentImported', (data) => {
        toast.success(
          `${data?.name || 'Torrent'}: lädt — wird nach Download einsortiert`,
        );
        onTorrentImported?.(data);
      });
      shell.on('torrentImportError', (e) =>
        toast.error('Import: ' + (e?.err || 'Import fehlgeschlagen')),
      );
    }
  };
  arm();
}
